// Contracts — приём кадра из TG-бота в каталог eyeq (ADR-0082).
//
// Одна ручка узкого ingest-класса: отдать байты и получить вердикт. Гейты
// стоят ДО записи (как у maps-ingest, ADR-0074) — отбитый кадр не оставляет
// ни объекта в R2, ни строки в каталоге, поэтому пробный кадр онбординга
// ничего не пачкает. Категории у кадра нет: лейн её не спрашивает (человек
// шлёт пачку фото, а не заполняет таксономию), её добирает AI-категоризация
// тем же путём, что у maps-кадров.
//
// Вердикт СЕРВЕРНЫЙ. Бот считает то же самое у себя (тем же
// `tgAppCheckFrame` из этого пакета) — но только чтобы объяснить человеку
// причину до отправки; авторитет здесь.
//
// ЛОКАЛЬ КАДРА определяет СЕРВЕР по координатам самого кадра, а не бот по
// пригласительной ссылке (решение владельца 2026-09-03: «нам не важно, как
// зашёл человек… страну мы его поймём по гео на кадре»). Поле `locale` в
// мете — ЗАЯВЛЕННОЕ: это только подсказка о языке внутри определённой по GPS
// страны. Неразобранный GPS и точки вне стран плана не принимаются.
import { z } from 'zod';
import { EyeqLocaleSchema } from './locales';
import { MeqCategorySchema, MeqSubCategorySchema } from './meta';
import { EyeqShotKindSchema } from '../vocab/eyeq';

/** Мета едет в base64-заголовке `X-Eyeq-Tg-Meta`, тело — сырые байты. Тот же
 *  транспорт, что у `/maps/frame`: multipart в воркере ради двух полей —
 *  лишняя точка отказа. */
export const EyeqTgFrameMetaSchema = z.object({
  locale: EyeqLocaleSchema,
  filename: z.string().trim().min(1).max(200),
  /** Кто сдал: `tg:<user_id>`. Пишется в `uploader_id`, поэтому выработка
   *  человека сводится без отдельного реестра. */
  uploader_id: z.string().trim().min(1).max(64),
  /** Как файл пришёл в Telegram. `photo` = сжатая картинка: метаданных там
   *  уже нет, и это отдельная, самая частая причина отказа. */
  transport: z.enum(['document', 'photo']),
  mime: z.string().max(120).default(''),
  /** Категория, если кампания её знает. Пусто = кадр без категории, её
   *  добирает AI-категоризация. */
  category: z.union([MeqCategorySchema, z.literal('')]).default(''),
  sub_category: z.union([MeqSubCategorySchema, z.literal('')]).default(''),
  /**
   * Связка сдачи: кадры, присланные как ОДНА работа. Регламент просит объект
   * плюс опознаватель (ценник, штрихкод, этикетка), и без этого поля «товар и
   * его ценник» неотличимы от двух разных товаров. Пусто = одиночный кадр.
   */
  submission_id: z.string().trim().max(96).default(''),
  /**
   * Что на кадре. Приёмник шлёт 'unknown' и НЕ угадывает: читаемый штрихкод
   * признаком опознавателя не является (у товара код напечатан на упаковке),
   * а ценник часто вообще без кода. Тип решают AI-категоризация и QA.
   */
  shot_kind: EyeqShotKindSchema.default('unknown'),
  /**
   * Коды, вычитанные из кадра НА КЛИЕНТЕ (воркер изображений не декодирует —
   * тот же контракт, что у submit). Именно код делает конкретную модель
   * товара машинно опознаваемой. Потолок 8: больше на кадре не бывает, а
   * лимит должен стоять на границе, а не в SQL-бинде.
   */
  codes: z.array(z.object({
    value: z.string().trim().min(1).max(256),
    code_type: z.string().trim().max(32).default(''),
  })).max(8).default([]),
});
export type EyeqTgFrameMeta = z.infer<typeof EyeqTgFrameMetaSchema>;

export const EyeqTgFrameResSchema = z.object({
  ok: z.boolean(),
  verdict: z.enum(['accepted', 'duplicate', 'rejected']),
  /** Причины отказа вокабуляром `TG_APP_NOTES` — тем же, по которому лейн
   *  выбирает текст подсказки в трёх языках. */
  notes: z.array(z.string()),
  asset_id: z.string().nullable(),
  existing_asset_id: z.string().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  gps_present: z.boolean(),
  captured_at: z.string().nullable(),
  /** EXIF-камера: нужна подсказке про мегапиксели («твой телефон умеет, дело
   *  в настройке»). Не PII — модель устройства, не человек. */
  make: z.string(),
  model: z.string(),
  /** Связка сдачи, как её записал каталог — эхо для журнала бота. */
  submission_id: z.string().default(''),
  /** Локаль, которую кадр в итоге получил. */
  locale: z.string().default(''),
  /** Страна, определённая по КООРДИНАТАМ кадра; '' — не опознана.
   *  Сами координаты не возвращаются и нигде не сохраняются (ADR-0039 §2). */
  geo_country: z.string().default(''),
  /** `geo` — подтверждена по кадру; `claimed` — только контекст раннего
   *  отказа, не подтверждение страны; `off_plan` — точка вне стран плана.
   *  accepted у участников возможен только с `geo`. */
  locale_source: z.enum(['geo', 'claimed', 'off_plan']).default('claimed'),
  /** Сколько кодов кадра осело в каталоге. */
  codes_stored: z.number().default(0),
});
export type EyeqTgFrameRes = z.infer<typeof EyeqTgFrameResSchema>;

// ── PUT /api/eyeq/upload/participantFrame (ingest) ─────────────────────────
// Кадры участников, пришедшие МИМО бота (чаты, архивы, «скинули папкой»):
// тот же приём, что у лейна бота (кабинетный набор гейтов, локаль по GPS,
// канал participant), но отказ НЕ теряется — байты ложатся в R2, в каталоге
// появляется строка status='rejected' с причинами. Владелец, 2026-09-05:
// «так или иначе занести и зафиксировать за участником».
export const EyeqParticipantFrameResSchema = EyeqTgFrameResSchema.extend({
  /** Отказ сохранён (байты в R2 + строка rejected). false у дубля: он уже есть. */
  stored_rejected: z.boolean().default(false),
});
export type EyeqParticipantFrameRes = z.infer<typeof EyeqParticipantFrameResSchema>;
